import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Session Store
 * Manages global session state for both EHR launch and standalone modes.
 *
 * Fields:
 * - currentPatient: FHIR Patient resource ID (from EHR launch context or selection)
 * - currentEncounter: FHIR Encounter resource ID (from launch context or created)
 * - userRole: 'provider' | 'patient' | 'admin'
 * - tenantId: Multi-tenant routing ID for standalone mode
 * - smartContext: Launch context details (patientId, encounterId, fhirServer, fhirUser, authMode)
 * - uiMode: 'full' | 'lite' (TV-LM-1) — gates whether the app renders the
 *   full Phase 1-5 surface (PatientPanel sidebar, Assessment/CarePlan/AIDocs/
 *   AIAgent/Billing tabs, waiting room, post-visit summary) or the simpler
 *   single-screen flow (collapsed PatientPanel, no tabs, queue → /visit
 *   direct skip). Set once at /launch from the `?mode=lite` query param;
 *   defaults to 'full'.
 *
 * Persisted to sessionStorage (not localStorage) so it clears on tab close.
 */
export const useSessionStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Session context
        currentPatient: null,
        currentEncounter: null,
        userRole: null,
        tenantId: null,

        // TV-LM-1 — UI mode (full vs lite). See file header for semantics.
        // Default 'full' so any launch path that doesn't pass ?mode= still
        // gets the canonical Phase 1-5 surface.
        uiMode: 'full',

        // SMART / auth context
        smartContext: {
          patientId: null,
          encounterId: null,
          fhirServer: null,
          fhirUser: null,
          authMode: null, // 'ehr' | 'standalone' | 'dev'
        },

        // Actions
        setCurrentPatient: (patientId) =>
          set({ currentPatient: patientId }, false, 'setCurrentPatient'),

        setCurrentEncounter: (encounterId) =>
          set({ currentEncounter: encounterId }, false, 'setCurrentEncounter'),

        setUserRole: (role) =>
          set({ userRole: role }, false, 'setUserRole'),

        setTenantId: (tenantId) =>
          set({ tenantId }, false, 'setTenantId'),

        /**
         * TV-LM-1 — flip the UI mode. Accepts the literal strings 'full'
         * or 'lite'; anything else is normalized to 'full' so a buggy
         * caller can't end up in a half-rendered state.
         */
        setUiMode: (mode) =>
          set(
            { uiMode: mode === 'lite' ? 'lite' : 'full' },
            false,
            'setUiMode',
          ),

        setSMARTContext: (context) =>
          set(
            (state) => ({
              smartContext: { ...state.smartContext, ...context },
            }),
            false,
            'setSMARTContext'
          ),

        /**
         * Clears session (on logout or session end).
         */
        clearSession: () =>
          set(
            {
              currentPatient: null,
              currentEncounter: null,
              userRole: null,
              tenantId: null,
              uiMode: 'full',
              smartContext: {
                patientId: null,
                encounterId: null,
                fhirServer: null,
                fhirUser: null,
                authMode: null,
              },
            },
            false,
            'clearSession'
          ),

        /**
         * Gets full session context snapshot.
         */
        getSessionContext: () => {
          const state = get();
          return {
            currentPatient: state.currentPatient,
            currentEncounter: state.currentEncounter,
            userRole: state.userRole,
            tenantId: state.tenantId,
            uiMode: state.uiMode,
            smartContext: state.smartContext,
          };
        },
      }),
      {
        name: 'televisit-session',
        // TV-LM-1 (2026-06-02) bumped to v3 — added uiMode field.
        // Persisted v2 blobs will drop the missing field and re-default
        // to 'full' via the store's initializer.
        version: 3,
        // Use sessionStorage so session clears on tab close
        storage: {
          getItem: (name) => {
            const value = sessionStorage.getItem(name);
            return value ? JSON.parse(value) : null;
          },
          setItem: (name, value) => {
            sessionStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: (name) => {
            sessionStorage.removeItem(name);
          },
        },
        partialize: (state) => ({
          currentPatient: state.currentPatient,
          currentEncounter: state.currentEncounter,
          userRole: state.userRole,
          tenantId: state.tenantId,
          uiMode: state.uiMode,
          smartContext: state.smartContext,
        }),
      }
    )
  )
);
